import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class HomeResolver implements Resolve<any> {
  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private useTransferState<T>(key: string, observable$: Observable<T>): Observable<T> {
    const STATE_KEY = makeStateKey<T>(key);

    if (this.transferState.hasKey(STATE_KEY)) {
      return of(this.transferState.get<T>(STATE_KEY, null as any));
    }

    return observable$.pipe(
      map(data => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(STATE_KEY, data);
        }
        return data;
      })
    );
  }

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const lang = route.url[0]?.path || 'en';

    // ✅ SSR-safe absolute paths
    const mainDataUrl = `/assets/${environment.projectName}/pages-components.json`;
    const metadataUrl = `/assets/${environment.projectName}/content/${lang}/home-page/meta-data.json`;

    return forkJoin({
      mainData: this.useTransferState(
        'homeMainData',
        this.http.get<any>(mainDataUrl).pipe(catchError(() => of({})))
      ),
      metadata: this.useTransferState(
        'homeMetaData',
        this.http.get<any>(metadataUrl).pipe(catchError(() => of({})))
      )
    }).pipe(
      switchMap(({ mainData, metadata }) => {
        const homeComponents = mainData?.homeComponents || [];

        if (homeComponents.length > 0) {
          const requests = homeComponents.map((item: any) => {
            const fileName = item.fileName;
            const subJsonPath = `/assets/${environment.projectName}/content/${lang}/${fileName}.json`;

            return this.useTransferState(
              `homeSubJson-${fileName}`,
              this.http.get(subJsonPath).pipe(
                map(details => ({ ...item, details })),
                catchError(error => {
                  console.error(`Failed to fetch ${fileName}:`, error);
                  return of({ ...item, details: null });
                })
              )
            );
          });

          return forkJoin(requests).pipe(
            map(componentsWithDetails => ({
              ...(mainData || {}),
              homeComponents: componentsWithDetails,
              metadata
            }))
          );
        } else {
          console.warn('No components found in homeComponents.');
          return of({
            ...(mainData || {}),
            metadata
          });
        }
      }),
      catchError(error => {
        console.error('Error fetching home page data:', error);
        return of(null);
      })
    );
  }
}
